import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle,
  Calendar,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { motion } from "framer-motion";
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/history")({
  component: HistoryPage,
});

function HistoryPage() {
  const currentLang = getCurrentLanguage();
  const { userId, sessionToken } = getStoredUserSession();

  const redemptions = useQuery(
    api.redemptions.getUserRedemptionHistory,
    userId ? { userId, sessionToken: sessionToken!, limit: 50 } : "skip"
  );

  const latestOffers = useQuery(api.offers.getLatestOffers, { limit: 3 });

  // Formatare dată
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return currentLang === "ro" ? "Azi" : "Today";
    } else if (diffDays === 1) {
      return currentLang === "ro" ? "Ieri" : "Yesterday";
    } else if (diffDays < 7) {
      return currentLang === "ro"
        ? `Acum ${diffDays} zile`
        : `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString(currentLang === "ro" ? "ro-RO" : "en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString(
      currentLang === "ro" ? "ro-RO" : "en-US",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  if (redemptions === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Grupează pe zile
  const groupedRedemptions: Record<string, typeof redemptions> = {};
  redemptions.forEach((r) => {
    const date = new Date(r.redeemedAt);
    const key = date.toISOString().split("T")[0];
    if (!groupedRedemptions[key]) {
      groupedRedemptions[key] = [];
    }
    groupedRedemptions[key].push(r);
  });

  return (
    <div className="px-4 py-6 pt-[180px]">
      {redemptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="relative w-full max-w-[280px] h-[200px] mb-8 flex items-center justify-center">
            {latestOffers ? (
              <>
                {/* Card stanga */}
                <motion.div
                  initial={{ rotate: -10, x: -20, opacity: 0 }}
                  animate={{ rotate: -12, x: -50, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="absolute top-0 w-[140px] h-[190px] rounded-2xl overflow-hidden shadow-xl bg-white border-4 border-white z-0"
                >
                  <img
                    src={latestOffers[1]?.imageUrl || latestOffers[0]?.imageUrl}
                    alt="Offer prev"
                    className="w-full h-full object-cover opacity-80"
                  />
                </motion.div>

                {/* Card dreapta */}
                <motion.div
                  initial={{ rotate: 10, x: 20, opacity: 0 }}
                  animate={{ rotate: 12, x: 50, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="absolute top-0 w-[140px] h-[190px] rounded-2xl overflow-hidden shadow-xl bg-white border-4 border-white z-0"
                >
                  <img
                    src={latestOffers[2]?.imageUrl || latestOffers[0]?.imageUrl}
                    alt="Offer next"
                    className="w-full h-full object-cover opacity-80"
                  />
                </motion.div>

                {/* Card centru */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="absolute top-[-10px] w-[150px] h-[210px] rounded-2xl overflow-hidden shadow-2xl bg-white border-4 border-white z-10"
                >
                  <img
                    src={latestOffers[0]?.imageUrl}
                    alt="Offer center"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                    <div className="w-3 h-3 bg-white rounded-full" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-white text-xs font-bold truncate">
                      {latestOffers[0]?.partner?.name}
                    </p>
                  </div>
                </motion.div>
              </>
            ) : (
              <div className="w-16 h-16 animate-pulse bg-gray-200 rounded-full" />
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-[280px]"
          >
            <h5 className="text-xl font-bold text-text mb-3">
              {currentLang === "ro"
                ? "Istoricul tău e gol"
                : "Your history is empty"}
            </h5>
            <p className="text-text-muted mb-8 leading-relaxed">
              {currentLang === "ro"
                ? "Activează ofertele la locațiile care îți plac și vor fi salvate aici pentru tine."
                : "Redeem offers at venues you like and they'll be saved here for you."}
            </p>

            <Link
              to="/app"
              className="px-8 py-3.5 bg-primary text-white rounded-full font-semibold shadow-primary hover:shadow-primary-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {currentLang === "ro" ? "Descoperă oferte" : "Discover offers"}
            </Link>
          </motion.div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedRedemptions).map(([date, items]) => (
            <div key={date}>
              {/* Date header */}
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-text-muted" />
                <span className="text-sm font-medium text-text-muted">
                  {formatDate(items[0].redeemedAt)}
                </span>
              </div>

              {/* Redemptions for this date */}
              <div className="space-y-3">
                {items.map((redemption) => (
                  <div
                    key={redemption._id}
                    className="bg-white rounded-xl p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={20} className="text-success" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text">
                          {redemption.partner?.name}
                        </p>
                        <p className="text-sm text-text-light truncate">
                          {typeof redemption.offer?.description === "object"
                            ? redemption.offer.description[currentLang as "ro" | "en"] || redemption.offer.description.ro
                            : redemption.offer?.description}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-bold rounded">
                            {redemption.discountType === "percent"
                              ? `${redemption.discountValue}%`
                              : redemption.benefitText}
                          </span>
                          <span className="text-xs text-text-muted">
                            {formatTime(redemption.redeemedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
